import { Injectable, OnDestroy } from '@angular/core';
import { AwallComponentEvent } from '../models/awallComponentEvent';
import { ComponentEvent } from '../models/enums/componentEvent';
import { Userstory } from '../models/userstory';
import { UserstoryService } from './userstory.service';
import { Listener, CommunicationService } from './communication.service';

/**
 * Handles drawing and updating the Dependency-Arrows between issues.
 */
@Injectable({
  providedIn: 'root',
  })
export class DependencyEditmodeService implements OnDestroy, Listener<AwallComponentEvent>{

  public static editModeActive = false;
  public static readonly componentId = 'DependencyEditmodeService';
  private selectedSourceIssue: Userstory;
  private selectedTargetIssue: Userstory;
  private readonly CSS_CLASSNAME_EDIT_MODE_ACTIVE = 'dependency-edit-mode-active';

  constructor(
    private userstoryService: UserstoryService,
    private communicationService: CommunicationService
  ) {
    this.toggleEditMode = this.toggleEditMode.bind(this);
    this.communicationService.registerComponent(DependencyEditmodeService.componentId, this);
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(DependencyEditmodeService.componentId);
  }

  public toggleEditMode(): void {
    DependencyEditmodeService.editModeActive = !DependencyEditmodeService.editModeActive;
    const event = new AwallComponentEvent(ComponentEvent.DEPENDENCY_EDITMODE_CHANGED, DependencyEditmodeService.editModeActive);
    this.communicationService.sendToAll(event);
    this.applyEditModeIssueStyle();
  }

  onIssueSelected(issue: Userstory, projectKey: string): void {
    // First issue clicked, set as sourceIssue
    if (!this.selectedSourceIssue) {
      this.selectedSourceIssue = issue;
    } else {
      const selectedSourceClickedAgain = (this.selectedSourceIssue?.id === issue.id);
      // Selected issue was clicked again, un-select it
      if (selectedSourceClickedAgain) {
        this.selectedSourceIssue = undefined;
      } else {
        // A second issue was clicked, set it as target and create the issuelink and reset selection
        this.selectedTargetIssue = issue;
        this.createDependencyAndResetSelection(projectKey);
        return;
      }
    }
    this.applyEditModeIssueStyle();
  }

  private createDependencyAndResetSelection(projectKey: string): void {
    this.applyEditModeIssueStyle();
    this.createDependency(this.selectedTargetIssue, projectKey, this.selectedSourceIssue.key);
    // Use timeout on resetting selected issues because else the target selection is not visible to the user (too fast)
    setTimeout(() => {
      this.selectedSourceIssue = undefined;
      this.selectedTargetIssue = undefined;
      this.applyEditModeIssueStyle();
    }, 620);
  }

  /**
   * Apply a differenty style to issues when editmode is active (except selected issues)
   */
  private applyEditModeIssueStyle(): void {
    const issueElements = Array.from(document.getElementsByTagName('app-issue'));
    issueElements.forEach(issueElement => {
      const taskCardId = issueElement.getAttribute('data-taskcardid');
      const isSelectedIssue = (taskCardId === this.selectedSourceIssue?.id.toString()
        || taskCardId === this.selectedTargetIssue?.id.toString());

      if (!DependencyEditmodeService.editModeActive || isSelectedIssue) {
        issueElement.classList.remove(this.CSS_CLASSNAME_EDIT_MODE_ACTIVE);
      } else {
        issueElement.classList.add(this.CSS_CLASSNAME_EDIT_MODE_ACTIVE);
      }
    });
  }

  /**
   * Create the dependency in the issuetracking-system.
   * This will trigger an Issue-Updated-Sync-Event allowing components to detect the modified issue
   */
  private createDependency(sourceIssue: Userstory, sourceIssueProjectKey: string, targetIssueKey: string): void {
    this.userstoryService.createIssueLink(sourceIssue.key, sourceIssueProjectKey, targetIssueKey).subscribe();
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }

    switch (event.name) {
      case ComponentEvent.MPCBOARD_CHANGED: {
        // We delay the action to make sure the mpc-board-change has finished (all html-elements update)
        // TODO: not very elegant. A mechanism to inform us at the correct time after change would be better
        if (DependencyEditmodeService.editModeActive) {
          setTimeout(() => { this.applyEditModeIssueStyle(); }, 30);
        }
        break;
      }
    }
  }

}
